import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { isAdmin, getRoleLabel as getRoleLabelUtil, UserRole, STAFF_ROLES } from '../utils/permissions';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const GET_USER = gql`
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      email
      firstName
      lastName
      phoneNumber
      role
      address {
        street1
        street2
        city
        state
        zipCode
        country
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      email
      firstName
      lastName
      phoneNumber
      address {
        street1
        street2
        city
        state
        zipCode
        country
      }
    }
  }
`;

// Type definitions for Apollo queries/mutations
interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: string;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

interface GetUserData {
  getUser: User;
}

interface UpdateUserData {
  updateUser: User;
}

export function UserEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  const { data: targetUserData, loading: userLoading } = useQuery<GetUserData>(GET_USER, {
    variables: { id },
    skip: !id,
  });
  const [updateUser, { loading: updating }] = useMutation<UpdateUserData>(UPDATE_USER);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showRoleConfirmation, setShowRoleConfirmation] = useState(false);

  const user = userData?.getCurrentUser;
  const targetUser = targetUserData?.getUser;

  // Determine if we're editing from clients page or admin page
  const isFromClientsPage = location.pathname.includes('/clients/');
  const isFromAdminPage = location.pathname.includes('/admin/users/');

  // Populate form when user data loads
  useEffect(() => {
    if (targetUser) {
      setFormData({
        email: targetUser.email || '',
        firstName: targetUser.firstName || '',
        lastName: targetUser.lastName || '',
        phoneNumber: targetUser.phoneNumber || '',
        role: targetUser.role || '',
        street1: targetUser.address?.street1 || '',
        street2: targetUser.address?.street2 || '',
        city: targetUser.address?.city || '',
        state: targetUser.address?.state || '',
        zipCode: targetUser.address?.zipCode || '',
        country: targetUser.address?.country || 'US',
      });
    }
  }, [targetUser]);

  // Check if user is CLIENT or PROSPECT (cannot be promoted to staff roles)
  const isClientOrProspect = targetUser?.role === UserRole.CLIENT || targetUser?.role === UserRole.PROSPECT;

  // Get available roles for the dropdown based on current user's role
  const getAvailableRoles = () => {
    if (isClientOrProspect) {
      // CLIENT/PROSPECT can only switch between CLIENT and PROSPECT
      return [
        { value: UserRole.CLIENT, label: getRoleLabelUtil(UserRole.CLIENT) },
        { value: UserRole.PROSPECT, label: getRoleLabelUtil(UserRole.PROSPECT) },
      ];
    }
    // Staff users can be changed to any role
    return [
      { value: UserRole.ADMIN, label: getRoleLabelUtil(UserRole.ADMIN) },
      { value: UserRole.ATTORNEY, label: getRoleLabelUtil(UserRole.ATTORNEY) },
      { value: UserRole.CASE_MANAGER, label: getRoleLabelUtil(UserRole.CASE_MANAGER) },
      { value: UserRole.OFFICE_MANAGER, label: getRoleLabelUtil(UserRole.OFFICE_MANAGER) },
      { value: UserRole.INQUIRY_MANAGER, label: getRoleLabelUtil(UserRole.INQUIRY_MANAGER) },
      { value: UserRole.LEAD_CASE_MANAGER, label: getRoleLabelUtil(UserRole.LEAD_CASE_MANAGER) },
      { value: UserRole.MEDICAL_RECORDS_COORDINATOR, label: getRoleLabelUtil(UserRole.MEDICAL_RECORDS_COORDINATOR) },
      { value: UserRole.PARALEGAL, label: getRoleLabelUtil(UserRole.PARALEGAL) },
      { value: UserRole.CLIENT, label: getRoleLabelUtil(UserRole.CLIENT) },
      { value: UserRole.PROSPECT, label: getRoleLabelUtil(UserRole.PROSPECT) },
    ];
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';

    // Email validation (if being edited)
    if (isEditingEmail) {
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    }

    // Address validation (optional but if provided, must be complete)
    if (formData.street1 || formData.city || formData.state || formData.zipCode) {
      if (!formData.street1) newErrors.street1 = 'Street address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // If email was changed, show confirmation dialog
    if (isEditingEmail && formData.email !== targetUser?.email) {
      setShowEmailConfirmation(true);
      return;
    }

    // If role was changed, show confirmation dialog
    if (isEditingRole && formData.role !== targetUser?.role) {
      setShowRoleConfirmation(true);
      return;
    }

    await saveChanges();
  };

  const saveChanges = async () => {
    try {
      const input: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
      };

      if (formData.phoneNumber) {
        input.phoneNumber = formData.phoneNumber;
      }

      // Include email if it was changed (admin only)
      if (isEditingEmail && formData.email !== targetUser?.email) {
        input.email = formData.email;
      }

      // Include role if it was changed (admin only)
      if (isEditingRole && formData.role !== targetUser?.role) {
        input.role = formData.role;
      }

      // Only include address if at least street1 is provided
      if (formData.street1) {
        input.address = {
          street1: formData.street1,
          street2: formData.street2 || null,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        };
      }

      await updateUser({ 
        variables: { 
          id,
          input,
        } 
      });

      alert('User updated successfully!');
      setShowEmailConfirmation(false);
      setShowRoleConfirmation(false);
      setIsEditingEmail(false);
      setIsEditingRole(false);
      
      // Navigate back to appropriate page
      if (isFromClientsPage && targetUser?.role === 'CLIENT') {
        navigate(`/clients/${id}`);
      } else if (isFromAdminPage) {
        navigate('/admin/users');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      setShowEmailConfirmation(false);
      setShowRoleConfirmation(false);
      alert(`Error updating user: ${error.message}`);
    }
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  if (!targetUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation user={user} />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">User not found</h2>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb />

        <Card>
          <CardHeader>
            <CardTitle>Edit User: {targetUser.firstName} {targetUser.lastName}</CardTitle>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-sm text-gray-600">Email: {targetUser.email}</p>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                {getRoleLabelUtil(targetUser.role)}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? 'border-red-500' : ''}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? 'border-red-500' : ''}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Role Section (Admin only) */}
              {user && isAdmin(user.role) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">User Role</h3>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Role
                      </label>
                      {!isEditingRole && (
                        <button
                          type="button"
                          onClick={() => setIsEditingRole(true)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit Role
                        </button>
                      )}
                      {isEditingRole && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingRole(false);
                            setFormData(prev => ({ ...prev, role: targetUser?.role || '' }));
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                    {isEditingRole ? (
                      <>
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-aalaw-red"
                        >
                          {getAvailableRoles().map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                        {isClientOrProspect && (
                          <p className="text-xs text-orange-600 mt-1">
                            ⚠️ Client and Prospect users cannot be promoted to staff roles.
                          </p>
                        )}
                        {!isClientOrProspect && formData.role !== targetUser?.role && (
                          <p className="text-xs text-orange-600 mt-1">
                            ⚠️ Warning: Changing role will update both Cognito and database.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <Input
                          value={getRoleLabelUtil(formData.role)}
                          disabled
                          className="bg-gray-100"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Role is locked. Click "Edit Role" to change (Admin only)
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Email {isEditingEmail && <span className="text-red-500">*</span>}
                      </label>
                      {user && isAdmin(user.role) && !isEditingEmail && (
                        <button
                          type="button"
                          onClick={() => setIsEditingEmail(true)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Edit Email
                        </button>
                      )}
                      {isEditingEmail && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingEmail(false);
                            setFormData(prev => ({ ...prev, email: targetUser?.email || '' }));
                            setErrors(prev => ({ ...prev, email: '' }));
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!isEditingEmail}
                      className={`${!isEditingEmail ? 'bg-gray-100' : ''} ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {!isEditingEmail && (
                      <p className="text-xs text-gray-500 mt-1">
                        Email is locked. Click "Edit Email" to change (Admin only)
                      </p>
                    )}
                    {isEditingEmail && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Warning: Changing email will update both Cognito and database. User must log in with new email.
                      </p>
                    )}
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <Input
                      name="street1"
                      value={formData.street1}
                      onChange={handleChange}
                      placeholder="123 Main St"
                      className={errors.street1 ? 'border-red-500' : ''}
                    />
                    {errors.street1 && (
                      <p className="text-red-500 text-xs mt-1">{errors.street1}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address Line 2
                    </label>
                    <Input
                      name="street2"
                      value={formData.street2}
                      onChange={handleChange}
                      placeholder="Apt, Suite, Unit, etc."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={errors.city ? 'border-red-500' : ''}
                      />
                      {errors.city && (
                        <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="CA"
                        maxLength={2}
                        className={errors.state ? 'border-red-500' : ''}
                      />
                      {errors.state && (
                        <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code
                      </label>
                      <Input
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        placeholder="12345"
                        className={errors.zipCode ? 'border-red-500' : ''}
                      />
                      {errors.zipCode && (
                        <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <Input
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="US"
                        maxLength={2}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updating}
                  className="flex-1"
                >
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Email Change Confirmation Modal */}
      {showEmailConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ⚠️ Confirm Email Change
            </h3>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm mb-2">
                  <strong>Warning:</strong> You are about to change the user's email address.
                </p>
                <div className="text-sm text-yellow-700 space-y-1">
                  <p><strong>From:</strong> {targetUser?.email}</p>
                  <p><strong>To:</strong> {formData.email}</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  <strong>Important:</strong>
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                  <li>This will update the email in both Cognito and the database</li>
                  <li>The user must log in with the NEW email address</li>
                  <li>The user will need to use their existing password</li>
                  <li>This action cannot be easily undone</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowEmailConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={saveChanges}
                  disabled={updating}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {updating ? 'Updating...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Confirmation Modal */}
      {showRoleConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ⚠️ Confirm Role Change
            </h3>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm mb-2">
                  <strong>Warning:</strong> You are about to change the user's role.
                </p>
                <div className="text-sm text-yellow-700 space-y-1">
                  <p><strong>From:</strong> {getRoleLabelUtil(targetUser?.role)}</p>
                  <p><strong>To:</strong> {getRoleLabelUtil(formData.role)}</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">
                  <strong>Important:</strong>
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                  <li>This will update the role in both Cognito and the database</li>
                  <li>The user's permissions will change immediately</li>
                  <li>The user may lose access to certain features</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRoleConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={saveChanges}
                  disabled={updating}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {updating ? 'Updating...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
